import { MONITOR_COLORS } from "../../config/functions";
import { Box, Chip, Typography, IconButton } from "@mui/material";
import LanIcon from "@mui/icons-material/Lan";
import { KeyboardArrowUp, KeyboardArrowDown } from "@mui/icons-material";
import { CounterIcon, LockerIcon, MachineIcon } from "../../../../../common/Icons";
import { useState, useRef, useEffect } from "react";

const typeConfig = {
    machine: {
        label: 'Máquina',
        backgroundColor: MONITOR_COLORS.background.machine,
        icon: MachineIcon,
    },
    counter: {
        label: 'Balcão',
        backgroundColor: MONITOR_COLORS.background.counter,
        icon: CounterIcon,
    },
    locker: {
        label: 'Armário',
        backgroundColor: MONITOR_COLORS.background.locker,
        icon: LockerIcon,
    }
};

function ErrorsRequest({ error }) {
    const config = typeConfig[error.tipo] ?? typeConfig.machine;
    const Icon = config.icon;

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1.5,
            px: 2,
            borderBottom: '1px solid ' + MONITOR_COLORS.tertiary + '55',
            '&:last-child': { borderBottom: 'none' },
            flexShrink: 0,
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
                <Box sx={{ position: 'relative', flexShrink: 0, marginRight: '1vw' }}>
                    <Icon sx={{ width: 20, height: 20, color: config.iconColor }} />
                    <Box sx={{
                        position: 'absolute', bottom: -3, right: -3,
                        width: 12, height: 12, borderRadius: '50%',
                        backgroundColor: MONITOR_COLORS.status.error.secondary,
                        border: '2px solid ' + MONITOR_COLORS.secondary,
                    }} />
                </Box>

                <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography sx={{
                            fontSize: { xs: '0.8rem', xl: '0.85rem' },
                            fontWeight: 600,
                            color: MONITOR_COLORS.text.primary,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: 220,
                        }}>
                            {error.erro}
                        </Typography>
                        <Typography sx={{
                            fontSize: '0.65rem',
                            color: MONITOR_COLORS.text.secondary,
                            flexShrink: 0,
                            px: 0.75, py: 0.1, borderRadius: 1,
                        }}>
                            ({error.equipamento})
                        </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.72rem', color: MONITOR_COLORS.text.secondary }}>
                        {error.empresa}
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ textAlign: 'right', flexShrink: 0, ml: 2 }}>
                <Typography sx={{ fontSize: '0.72rem', color: MONITOR_COLORS.text.secondary }}>
                    {error.tempo_atras}
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', color: MONITOR_COLORS.text.primary }}>
                    {error.horario}
                </Typography>
            </Box>
        </Box>
    );
}

export function MonitorErrorsRequest({ requestErrors }) {
    const ultimos = requestErrors?.ultimos ?? [];
    const [page, setPage] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(4);
    const containerRef = useRef(null);

    useEffect(() => { setPage(0); }, [ultimos.length]);

    useEffect(() => {
        const observer = new ResizeObserver(entries => {
            for (const entry of entries) {
                const height = entry.contentRect.height;
                // Cada linha tem ~56px de altura
                const rows = Math.max(1, Math.floor(height / 56));
                setItemsPerPage(rows);
            }
        });
        if (containerRef.current) observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);

    const totalPages = Math.ceil(ultimos.length / itemsPerPage);
    const paginated = ultimos.slice(page * itemsPerPage, (page + 1) * itemsPerPage);
    const canGoUp = page > 0;
    const canGoDown = page < totalPages - 1;

    return (
        <Box sx={{
            backgroundColor: MONITOR_COLORS.secondary,
            border: '1px solid ' + MONITOR_COLORS.tertiary,
            borderRadius: 3,
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%',
            marginTop: '2vh',
            marginRight: '5vw'
        }}>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                flexShrink: 0,
                backgroundColor: MONITOR_COLORS.primary,
                borderRadius: '12px 12px 0 0',
                borderBottom: '1px solid ' + MONITOR_COLORS.tertiary,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LanIcon sx={{ color: MONITOR_COLORS.text.primary, fontSize: { xs: 18, xl: 22 }, marginRight: '1vw' }} />
                    <Typography sx={{ fontWeight: 700, color: MONITOR_COLORS.text.primary, fontSize: { xs: '0.9rem', xl: '1rem' } }}>
                        Últimos Erros de Request
                    </Typography>
                </Box>

                {ultimos.length > 0 && (
                    <Chip
                        label={ultimos.length}
                        size="small"
                        sx={{
                            backgroundColor: MONITOR_COLORS.status.error.primary,
                            color: MONITOR_COLORS.status.error.secondary,
                            fontWeight: 700,
                            fontSize: '0.8rem',
                            height: 24,
                        }}
                    />
                )}
            </Box>

            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', minHeight: 0, overflow: 'hidden' }}>
                <Box ref={containerRef} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                    {ultimos.length === 0 ? (
                        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography sx={{ fontSize: '0.8rem', color: MONITOR_COLORS.text.secondary }}>
                                Sem erros ✓
                            </Typography>
                        </Box>
                    ) : (
                        paginated.map((eq, i) => (
                            <ErrorsRequest key={i} error={eq} />
                        ))
                    )}
                </Box>

                {totalPages > 1 && (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        px: 1,
                        borderLeft: '1px solid ' + MONITOR_COLORS.tertiary + '55',
                    }}>
                        <Typography sx={{ fontSize: '0.65rem', color: MONITOR_COLORS.text.secondary, fontFamily: 'monospace' }}>
                            {page + 1}/{totalPages}
                        </Typography>

                        <IconButton
                            onClick={() => setPage(p => p - 1)}
                            disabled={!canGoUp}
                            size="small"
                            sx={{
                                color: canGoUp ? MONITOR_COLORS.text.primary : MONITOR_COLORS.tertiary,
                                backgroundColor: canGoUp ? MONITOR_COLORS.tertiary + '33' : 'transparent',
                                border: '1px solid ' + (canGoUp ? MONITOR_COLORS.tertiary : MONITOR_COLORS.tertiary + '44'),
                                '&:hover': { backgroundColor: canGoUp ? MONITOR_COLORS.tertiary + '66' : 'transparent' },
                                transition: 'all 0.2s',
                                width: 28,
                                height: 28,
                            }}
                        >
                            <KeyboardArrowUp fontSize="small" />
                        </IconButton>

                        <IconButton
                            onClick={() => setPage(p => p + 1)}
                            disabled={!canGoDown}
                            size="small"
                            sx={{
                                color: canGoDown ? MONITOR_COLORS.text.primary : MONITOR_COLORS.tertiary,
                                backgroundColor: canGoDown ? MONITOR_COLORS.tertiary + '33' : 'transparent',
                                border: '1px solid ' + (canGoDown ? MONITOR_COLORS.tertiary : MONITOR_COLORS.tertiary + '44'),
                                '&:hover': { backgroundColor: canGoDown ? MONITOR_COLORS.tertiary + '66' : 'transparent' },
                                transition: 'all 0.2s',
                                width: 28,
                                height: 28,
                            }}
                        >
                            <KeyboardArrowDown fontSize="small" />
                        </IconButton>
                    </Box>
                )}
            </Box>
        </Box>
    );
}